import type {
  ForgeProvider,
  RunForgeCredentialView,
  RunForgeCredentialViewReason,
  TaskExecutionSummary,
} from '@/lib/api/generated/model';
import { PERSONAL_ACCOUNT_COPY } from '@/features/identity';

/**
 * Which account a run acted as on its forges (spec 159 US3, research R7).
 * `label` is the short mark on the run's detail line; `lines` say, one per
 * forge, whom the run acted as or why it fell back to the shared account.
 */
export interface RunCredentialMark {
  kind: 'personal' | 'shared' | 'required';
  label: string;
  lines: string[];
}

const PROVIDER_NAMES: Record<ForgeProvider, string> = {
  gitlab: 'GitLab',
  github: 'GitHub',
};

function providerName(provider: ForgeProvider): string {
  return PROVIDER_NAMES[provider] ?? provider;
}

function reasonWords(
  reason: RunForgeCredentialViewReason | undefined,
  provider: string,
): string {
  switch (reason) {
    case 'not_linked':
      return 'not linked';
    case 'broken':
      return 'the link needs relinking';
    case 'expired':
      return 'the token expired';
    case 'inactive_owner':
      return 'the person is no longer active';
    case 'revoked':
      return 'removed by an administrator';
    case 'unreachable':
      return `${provider} could not be reached`;
    default:
      return 'shared account';
  }
}

function lineFor(credential: RunForgeCredentialView): string {
  const provider = providerName(credential.provider);
  const where = `${provider} (${credential.host})`;
  if (credential.source === 'personal' && credential.forge_username) {
    return `${where}: acting as ${credential.forge_username}`;
  }
  if (credential.source === 'personal')
    return `${where}: acting as the linked account`;
  return `${where}: ${reasonWords(credential.reason, provider)}`;
}

function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** `null` when the run says nothing about its forge credentials. */
export function runCredentialMark(
  execution: Pick<
    TaskExecutionSummary,
    'credential_source' | 'forge_credentials'
  >,
): RunCredentialMark | null {
  const source = execution.credential_source;
  const credentials = execution.forge_credentials ?? [];
  if (source == null || source === 'none' || credentials.length === 0) {
    return null;
  }
  const lines = credentials.map(lineFor);
  // The workspace required the person's own account and a forge had none
  // usable, so the run never started (spec 160): never "shared account".
  if (source === 'required') {
    return { kind: 'required', label: PERSONAL_ACCOUNT_COPY.runMark, lines };
  }
  const personal = [
    ...new Set(
      credentials
        .filter((c) => c.source === 'personal')
        .map((c) => providerName(c.provider)),
    ),
  ];
  if (personal.length > 0) {
    return { kind: 'personal', label: `via ${joinNames(personal)}`, lines };
  }
  return { kind: 'shared', label: 'shared account', lines };
}
